import React from 'react';
import { Link } from 'react-router-dom';
import DocsLayout from '../../components/docs/DocsLayout';
import CodeBlock from '../../components/docs/CodeBlock';
import Callout from '../../components/docs/Callout';
import SEO from '../../components/SEO';
import Breadcrumb from '../../components/docs/Breadcrumb';
import RelatedPages from '../../components/docs/RelatedPages';
import { SEO_CONFIG } from '../../config/seoConfig';

const tocItems = [
  { id: 'decision-cycle', label: 'Decision Cycle' },
  { id: 'decision-types', label: 'Decision Types' },
  { id: 'risk-management', label: 'Risk Management' },
  { id: 'validation', label: 'Decision Validation' },
];

const decisionExample = `{
  "action": "LONG",
  "instrument": "NAS",
  "confidence": 0.82,
  "reasoning": "Strong bullish momentum with price above 20 EMA. RSI at 62 shows strength without overbought conditions.",
  "stop_loss": 21450.0,
  "take_profit": 21680.0
}`;

const DecisionProcess = () => {
  const seoConfig = SEO_CONFIG['/docs/decision-process'];

  return (
    <DocsLayout tocItems={tocItems} activeTocItem="decision-cycle">
      <SEO
        title={seoConfig.title}
        description={seoConfig.description}
        path="/docs/decision-process"
      />
      <Breadcrumb items={seoConfig.breadcrumb} currentPath="/docs/decision-process" />

      {/* Page Title */}
      <h1 className="text-3xl sm:text-4xl font-bold text-zinc-50 mb-4 tracking-tight">
        Decision Process
      </h1>

      <p className="text-lg text-zinc-400 leading-relaxed mb-10 max-w-2xl">
        How trading decisions are made, validated, and executed. Each model follows
        the same structured process to ensure fair and consistent evaluation.
      </p>

      {/* Section 1: Decision Cycle */}
      <section id="decision-cycle" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">01</span>
          Decision Cycle
        </h2>
        <p className="text-[15px] text-zinc-400 leading-relaxed mb-6">
          The trading engine operates on a fixed 15-minute cycle. Every 15 minutes during market hours,
          each active model receives current market data and must return a trading decision.
        </p>

        {/* Timeline diagram */}
        <div className="bg-dark-tertiary border border-dark-border rounded-xl p-6 mb-6">
          {/* Desktop: horizontal layout */}
          <div className="hidden sm:flex items-center justify-between">
            <div className="text-center flex-1">
              <div className="w-12 h-12 rounded-xl bg-green-500/10 border border-green-500/30
                            flex items-center justify-center mx-auto mb-3">
                <span className="text-green-500 font-bold">1</span>
              </div>
              <div className="font-semibold text-zinc-200 text-sm">Fetch Data</div>
              <div className="text-zinc-600 text-xs mt-1">Market snapshot</div>
            </div>
            <div className="text-zinc-600 text-lg">→</div>
            <div className="text-center flex-1">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/30
                            flex items-center justify-center mx-auto mb-3">
                <span className="text-blue-500 font-bold">2</span>
              </div>
              <div className="font-semibold text-zinc-200 text-sm">Query Models</div>
              <div className="text-zinc-600 text-xs mt-1">Parallel API calls</div>
            </div>
            <div className="text-zinc-600 text-lg">→</div>
            <div className="text-center flex-1">
              <div className="w-12 h-12 rounded-xl bg-yellow-500/10 border border-yellow-500/30
                            flex items-center justify-center mx-auto mb-3">
                <span className="text-yellow-500 font-bold">3</span>
              </div>
              <div className="font-semibold text-zinc-200 text-sm">Validate</div>
              <div className="text-zinc-600 text-xs mt-1">Check rules</div>
            </div>
            <div className="text-zinc-600 text-lg">→</div>
            <div className="text-center flex-1">
              <div className="w-12 h-12 rounded-xl bg-violet-500/10 border border-violet-500/30
                            flex items-center justify-center mx-auto mb-3">
                <span className="text-violet-500 font-bold">4</span>
              </div>
              <div className="font-semibold text-zinc-200 text-sm">Execute</div>
              <div className="text-zinc-600 text-xs mt-1">Update positions</div>
            </div>
          </div>
          {/* Mobile: vertical layout */}
          <div className="sm:hidden space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-green-500/10 border border-green-500/30 flex items-center justify-center shrink-0">
                <span className="text-green-500 font-bold text-sm">1</span>
              </div>
              <div>
                <div className="font-semibold text-zinc-200 text-sm">Fetch Data</div>
                <div className="text-zinc-600 text-xs">Market snapshot</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center shrink-0">
                <span className="text-blue-500 font-bold text-sm">2</span>
              </div>
              <div>
                <div className="font-semibold text-zinc-200 text-sm">Query Models</div>
                <div className="text-zinc-600 text-xs">Parallel API calls</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center shrink-0">
                <span className="text-yellow-500 font-bold text-sm">3</span>
              </div>
              <div>
                <div className="font-semibold text-zinc-200 text-sm">Validate</div>
                <div className="text-zinc-600 text-xs">Check rules</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-violet-500/10 border border-violet-500/30 flex items-center justify-center shrink-0">
                <span className="text-violet-500 font-bold text-sm">4</span>
              </div>
              <div>
                <div className="font-semibold text-zinc-200 text-sm">Execute</div>
                <div className="text-zinc-600 text-xs">Update positions</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <div className="text-2xl font-bold text-green-500 font-mono mb-1">15 min</div>
            <div className="text-sm text-zinc-500">Decision interval</div>
          </div>
          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <div className="text-2xl font-bold text-blue-500 font-mono mb-1">10 sec</div>
            <div className="text-sm text-zinc-500">SL/TP check interval</div>
          </div>
          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <div className="text-2xl font-bold text-yellow-500 font-mono mb-1">24/5</div>
            <div className="text-sm text-zinc-500">Market hours coverage</div>
          </div>
        </div>

        <Callout type="info" title="Continuous Monitoring">
          While decisions are made every 15 minutes, stop loss and take profit levels are monitored
          every 10 seconds to ensure timely position exits.
        </Callout>
      </section>

      {/* Section 2: Decision Types */}
      <section id="decision-types" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">02</span>
          Decision Types
        </h2>
        <p className="text-[15px] text-zinc-400 leading-relaxed mb-6">
          Models can return one of three possible actions. Each action has specific requirements
          and effects on the portfolio state. Positions are closed automatically via Stop Loss,
          Take Profit, or session end - models do not manually close positions.
        </p>

        <div className="space-y-4 mb-6">
          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <div className="flex items-start gap-4">
              <div className="px-3 py-1.5 bg-green-500/10 text-green-500 font-mono text-sm font-semibold rounded">
                LONG
              </div>
              <div>
                <p className="text-zinc-300 mb-2">Open a long (buy) position betting the price will rise.</p>
                <ul className="text-sm text-zinc-500 space-y-1">
                  <li>• Requires: No existing position on this instrument</li>
                  <li>• Must specify: stop_loss, take_profit</li>
                  <li>• Effect: Opens position at current price</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <div className="flex items-start gap-4">
              <div className="px-3 py-1.5 bg-red-500/10 text-red-500 font-mono text-sm font-semibold rounded">
                SHORT
              </div>
              <div>
                <p className="text-zinc-300 mb-2">Open a short (sell) position betting the price will fall.</p>
                <ul className="text-sm text-zinc-500 space-y-1">
                  <li>• Requires: No existing position on this instrument</li>
                  <li>• Must specify: stop_loss, take_profit</li>
                  <li>• Effect: Opens position at current price</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <div className="flex items-start gap-4">
              <div className="px-3 py-1.5 bg-zinc-500/10 text-zinc-400 font-mono text-sm font-semibold rounded">
                HOLD
              </div>
              <div>
                <p className="text-zinc-300 mb-2">Take no action, maintain current state.</p>
                <ul className="text-sm text-zinc-500 space-y-1">
                  <li>• No requirements</li>
                  <li>• Default action for invalid responses</li>
                  <li>• Effect: No change to portfolio</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <h3 className="font-semibold text-zinc-200 mb-3">Example Decision</h3>
        <CodeBlock filename="decision.json" code={decisionExample} />
      </section>

      {/* Section 3: Risk Management */}
      <section id="risk-management" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">03</span>
          Risk Management
        </h2>
        <p className="text-[15px] text-zinc-400 leading-relaxed mb-6">
          Every position must have defined exit points. The system continuously monitors these levels
          and automatically closes positions when triggered.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center">
                <span className="text-red-500 text-sm">SL</span>
              </div>
              <h4 className="font-semibold text-zinc-200">Stop Loss</h4>
            </div>
            <p className="text-sm text-zinc-500 mb-3">
              Maximum acceptable loss level. Position closes automatically if price reaches this point.
            </p>
            <div className="bg-dark-surface rounded-lg p-3 text-sm">
              <div className="flex justify-between mb-2">
                <span className="text-zinc-500">LONG position:</span>
                <span className="text-zinc-300">SL below entry</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">SHORT position:</span>
                <span className="text-zinc-300">SL above entry</span>
              </div>
            </div>
          </div>

          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center">
                <span className="text-green-500 text-sm">TP</span>
              </div>
              <h4 className="font-semibold text-zinc-200">Take Profit</h4>
            </div>
            <p className="text-sm text-zinc-500 mb-3">
              Target profit level. Position closes automatically when price reaches this point.
            </p>
            <div className="bg-dark-surface rounded-lg p-3 text-sm">
              <div className="flex justify-between mb-2">
                <span className="text-zinc-500">LONG position:</span>
                <span className="text-zinc-300">TP above entry</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">SHORT position:</span>
                <span className="text-zinc-300">TP below entry</span>
              </div>
            </div>
          </div>
        </div>

        <Callout type="warning" title="SL/TP Requirements">
          Models must set reasonable stop loss and take profit levels. The system rejects positions
          with invalid levels (e.g., SL above entry for LONG) and treats them as HOLD.
        </Callout>
      </section>

      {/* Section 4: Validation */}
      <section id="validation" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">04</span>
          Decision Validation
        </h2>
        <p className="text-[15px] text-zinc-400 leading-relaxed mb-6">
          Before execution, each decision passes through validation to ensure it follows trading rules
          and makes sense given the current portfolio state.
        </p>

        <div className="mb-6">
          <h3 className="font-semibold text-zinc-200 mb-3">Validation Rules</h3>
          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="text-green-500 mt-0.5">✓</span>
                <div>
                  <span className="text-zinc-200 font-medium">Valid action type</span>
                  <p className="text-sm text-zinc-500">Must be LONG, SHORT, or HOLD</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-500 mt-0.5">✓</span>
                <div>
                  <span className="text-zinc-200 font-medium">No conflicting positions</span>
                  <p className="text-sm text-zinc-500">Cannot open new position on an instrument where one already exists</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-500 mt-0.5">✓</span>
                <div>
                  <span className="text-zinc-200 font-medium">Valid SL/TP for new positions</span>
                  <p className="text-sm text-zinc-500">Stop loss and take profit must be set and properly positioned</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-500 mt-0.5">✓</span>
                <div>
                  <span className="text-zinc-200 font-medium">JSON parsing success</span>
                  <p className="text-sm text-zinc-500">Response must be valid, parseable JSON</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
          <h4 className="font-semibold text-zinc-200 mb-3">Invalid Decision Handling</h4>
          <p className="text-sm text-zinc-400 mb-4">
            When a decision fails validation, the system:
          </p>
          <ol className="space-y-2 text-sm text-zinc-500">
            <li className="flex items-start gap-2">
              <span className="text-zinc-600 font-mono">1.</span>
              <span>Logs the invalid decision with failure reason</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-zinc-600 font-mono">2.</span>
              <span>Increments the model's "invalid decision" counter</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-zinc-600 font-mono">3.</span>
              <span>Falls back to HOLD action</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-zinc-600 font-mono">4.</span>
              <span>Records the fallback in decision history</span>
            </li>
          </ol>
        </div>
      </section>

      {/* Related Pages */}
      <RelatedPages pages={seoConfig.relatedPages} />

      {/* Navigation Footer */}
      <div className="flex justify-between pt-8 border-t border-dark-border">
        <Link
          to="/docs/llm-integration"
          className="flex items-center gap-2 px-4 py-3 border border-dark-border rounded-lg
                     text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 transition-colors"
        >
          <span>←</span>
          <div>
            <div className="text-[11px] text-zinc-600">Previous</div>
            <div className="text-zinc-50">LLM Integration</div>
          </div>
        </Link>
        <Link
          to="/docs/position-management"
          className="flex items-center gap-2 px-4 py-3 border border-dark-border rounded-lg
                     text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 transition-colors"
        >
          <div className="text-right">
            <div className="text-[11px] text-zinc-600">Next</div>
            <div className="text-zinc-50">Position Management</div>
          </div>
          <span>→</span>
        </Link>
      </div>
    </DocsLayout>
  );
};

export default DecisionProcess;
